import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
    OneTelecomAreasResponse,
    OneTelecomBuildingsResponse,
    OneTelecomFloorsResponse,
    OneTelecomLineFeature,
    OneTelecomLinesResponse,
    OneTelecomSitesResponse
} from 'src/app/models/one-telecom';
import {
    IndoorArea,
    IndoorBuilding,
    IndoorFloor,
    IndoorLine,
    IndoorMapDataset,
    IndoorSite,
} from 'src/app/models/indoor-map';

@Injectable({
    providedIn: 'root'
})
export class IndoorMapDataService {
    private static readonly LINES_PAGE_SIZE = 19999;

    private readonly _indoorMap$: Observable<IndoorMapDataset>;

    constructor(private readonly http: HttpClient) {
        // Load only sites on init; buildings load on site selection, floors/areas/lines load on demand
        this._indoorMap$ = this._loadSiteLayer$().pipe(
            map((siteResponse) => this._toIndoorMap(siteResponse)),
        );
    }

    public getIndoorMap$(): Observable<IndoorMapDataset> {
        return this._indoorMap$;
    }

    /**
     * Load buildings for a specific site when user selects the site from dropdown
     * @param siteId The SITE_ID from the API response
     * @returns Observable with buildings for the selected site, transformed to IndoorBuilding[]
     */
    public loadBuildingsForSite$(siteId: number, existingDataset: IndoorMapDataset): Observable<IndoorBuilding[]> {
        return this._loadBuildingsForSite$(siteId).pipe(
            map((buildingResponse) => {
                const buildings: IndoorBuilding[] = (buildingResponse.features ?? [])
                    .filter((feature) => !!feature.geometry?.rings?.length)
                    .map((feature, index) => {
                        const rings = this._toPolygonRings(feature.geometry!.rings);
                        // Find the site by matching the API SITE_ID from the response
                        const owningSite = existingDataset.sites.find(site => site.apiSiteId === feature.attributes.SITE_ID);

                        return {
                            id: feature.attributes.BUILDING_ID !== undefined && feature.attributes.BUILDING_ID !== null
                                ? String(feature.attributes.BUILDING_ID)
                                : `building-${index}`,
                            name: feature.attributes.NAME,
                            siteId: owningSite?.id ?? 'site-0',
                            siteName: owningSite?.name ?? 'Site',
                            rings,
                        };
                    });
                return buildings;
            }),
            catchError((error) => {
                console.error(`Failed to load buildings for site ${siteId}:`, error);
                return of([]);
            })
        );
    }

    private _toIndoorMap(siteLayer: OneTelecomSitesResponse): IndoorMapDataset {
        const sites: IndoorSite[] = siteLayer.features
            .map((feature, index) => ({
                id: `site-${index}`,
                name: feature.attributes.NAME,
                apiSiteId: feature.attributes.SITE_ID,
                rings: this._toPolygonRings(feature.geometry?.rings ?? []),
            }));

        return {
            sites,
            buildings: [],
            floors: [],
        };
    }

    private _loadSiteLayer$(): Observable<OneTelecomSitesResponse> {
        const params = new HttpParams()
            .set('where', "EXT_ID like '%'")
            .set('outFields', 'SITE_ID,NAME');
        return this.http.get<OneTelecomSitesResponse>(`${environment.API_ENDPOINT}/oneTelecom/sites`, { params });
    }



    private _loadBuildingsForSite$(siteId: number): Observable<OneTelecomBuildingsResponse> {
        const params = new HttpParams()
            .set('where', `SITE_ID=${siteId}`)
            .set('outFields', 'SITE_ID,BUILDING_ID,NAME');
        
        return this.http.get<OneTelecomBuildingsResponse>(`${environment.API_ENDPOINT}/oneTelecom/buildings`, { params }).pipe(
            catchError((error) => {
                console.error(`Failed to load buildings for site ${siteId}:`, error);
                return of({ features: [] } as unknown as OneTelecomBuildingsResponse);
            })
        );
    }

    public loadFloorsForBuilding$(buildingId: string, existingDataset: IndoorMapDataset): Observable<IndoorFloor[]> {
        return this._loadFloorsForBuilding$(buildingId).pipe(
            map((floorResponse) => {
                const floors: IndoorFloor[] = (floorResponse.features ?? [])
                    .map((feature, index) => {
                        // Find the building by matching the API BUILDING_ID from the response
                        const owningBuilding = existingDataset.buildings.find(
                            building => building.id === String(feature.attributes.BUILDING_ID)
                        );

                        return {
                            id: feature.attributes.FLOOR_ID !== undefined && feature.attributes.FLOOR_ID !== null
                                ? String(feature.attributes.FLOOR_ID)
                                : `floor-${index}`,
                            level: feature.attributes.FLOOR_LEVEL,
                            label: feature.attributes.FLOOR_LEVEL,
                            siteId: owningBuilding?.siteId ?? 'site-0',
                            siteName: owningBuilding?.siteName ?? 'Site',
                            buildingId: String(feature.attributes.BUILDING_ID),
                            buildingName: owningBuilding?.name ?? 'Building',
                            areas: [],
                            lines: [],
                            labels: [],
                        };
                    });
                return floors;
            }),
            catchError((error) => {
                console.error(`Failed to load floors for building ${buildingId}:`, error);
                return of([]);
            })
        );
    }

    private _loadFloorsForBuilding$(buildingId: string): Observable<OneTelecomFloorsResponse> {
        const params = new HttpParams()
            .set('where', `BUILDING_ID=${buildingId}`)
            .set('outFields', 'BUILDING_ID,FLOOR_ID,FLOOR_LEVEL');
        
        return this.http.get<OneTelecomFloorsResponse>(`${environment.API_ENDPOINT}/oneTelecom/floors`, { params }).pipe(
            catchError((error) => {
                console.error(`Failed to load floors for building ${buildingId}:`, error);
                return of({ features: [] } as unknown as OneTelecomFloorsResponse);
            })
        );
    }

    public loadAreasAndLinesForFloor$(
        floorId: string,
        existingFloor: IndoorFloor
    ): Observable<IndoorFloor> {
        return forkJoin({
            areas: this._loadAreasForFloor$(floorId),
            lines: this._loadLinesForFloor$(floorId),
        }).pipe(
            map(({ areas, lines }) => {
                const mappedAreas: IndoorArea[] = (areas.features ?? [])
                    .filter((feature) => !!feature.geometry?.rings?.length)
                    .map((feature, index) => ({
                        id: `area-${floorId}-${index}`,
                        label: `Area ${index + 1}`,
                        rings: this._toPolygonRings(feature.geometry!.rings),
                        fillColor: [59, 130, 246, 0.12] as [number, number, number, number],
                        outlineColor: [30, 64, 175, 0.95] as [number, number, number, number],
                    }));

                return { ...existingFloor, areas: mappedAreas, lines, labels: [] };
            }),
            catchError((error) => {
                console.error(`Failed to load areas/lines/texts for floor ${floorId}:`, error);
                return of({ ...existingFloor, areas: [], lines: [], labels: [] });
            })
        );
    }

    private _loadAreasForFloor$(floorId: string): Observable<OneTelecomAreasResponse> {
        const params = new HttpParams()
            .set('where', `FLOOR_ID=${floorId}`)
            .set('outFields', 'FLOOR_ID,FLOOR_LEVEL');
        return this.http.get<OneTelecomAreasResponse>(`${environment.API_ENDPOINT}/oneTelecom/areas`, { params }).pipe(
            catchError((error) => {
                console.error(`Failed to load areas for floor ${floorId}:`, error);
                return of({ features: [] } as unknown as OneTelecomAreasResponse);
            })
        );
    }

    private _loadLinesForFloor$(floorId: string): Observable<IndoorLine[]> {
        return this._loadLinesForFloorPage$(
            floorId,
            0,
            IndoorMapDataService.LINES_PAGE_SIZE,
            []
        ).pipe(
            catchError((error) => {
                console.error(`Failed to load lines for floor ${floorId}:`, error);
                return of([]);
            })
        );
    }

    private _loadLinesForFloorPage$(
        floorId: string,
        resultOffset: number,
        resultRecordCount: number,
        accumulatedLines: IndoorLine[]
    ): Observable<IndoorLine[]> {
        const params = new HttpParams()
            .set('where', `FLOOR_ID=${floorId}`)
            .set('outFields', 'FLOOR_ID,FLOOR_LEVEL')
            .set('resultOffset', resultOffset.toString())
            .set('resultRecordCount', resultRecordCount.toString());

        return this.http.get<OneTelecomLinesResponse>(`${environment.API_ENDPOINT}/oneTelecom/lines`, { params }).pipe(
            map((response) => {
                const pageLines: IndoorLine[] = (response.features ?? [])
                    .filter((feature): feature is OneTelecomLineFeature & { geometry: NonNullable<OneTelecomLineFeature['geometry']> } => !!feature.geometry?.paths?.length)
                    .map((feature: OneTelecomLineFeature, index: number) => ({
                        id: `line-${floorId}-${resultOffset + index}`,
                        paths: this._toPolylinePaths(feature.geometry!.paths as number[][][]),
                        color: [15, 23, 42, 0.9] as [number, number, number, number],
                    }));

                const allLines = [...accumulatedLines, ...pageLines];

                // ArcGIS returns exceededTransferLimit=true when more pages are available.
                if (response.exceededTransferLimit) {
                    return this._loadLinesForFloorPage$(
                        floorId,
                        resultOffset + IndoorMapDataService.LINES_PAGE_SIZE,
                        resultRecordCount,
                        allLines
                    );
                }

                return of(allLines);
            }),
            switchMap((lines) => lines)
        );
    }

    private _toPolygonRings(rings: number[][][]): Array<Array<[number, number]>> {
        return rings.map((ring) => ring.map(([longitude, latitude]) => [longitude, latitude] as [number, number]));
    }

    private _toPolylinePaths(paths: number[][][]): Array<Array<[number, number]>> {
        return paths.map((path) => path.map(([longitude, latitude]) => [longitude, latitude] as [number, number]));
    }

}